import { App } from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError } from '@/services/api/client';
import * as projectReportsApi from '@/services/api/project-reports';
import * as uploadsApi from '@/services/api/uploads';
import { useAuthStore } from '@/stores/auth.store';
import type { ProjectReportComment, ProjectReportRecord } from '@/types/project-report';
import { canCommentProjectReport } from '@/utils/project-report-permissions';

export function useProjectReportDetail(projectReportId: string | undefined) {
  const { message } = App.useApp();
  const currentUser = useAuthStore((state) => state.user);

  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState<ProjectReportRecord | null>(null);
  const [comments, setComments] = useState<ProjectReportComment[]>([]);
  const [imageUrls, setImageUrls] = useState<Record<string, string>>({});
  const imageUrlsRef = useRef<Record<string, string>>({});

  const replaceImageUrls = useCallback((next: Record<string, string>) => {
    Object.values(imageUrlsRef.current).forEach((url) => {
      if (url) URL.revokeObjectURL(url);
    });
    imageUrlsRef.current = next;
    setImageUrls(next);
  }, []);

  const canComment = canCommentProjectReport(currentUser, report);

  const loadDetail = useCallback(async () => {
    if (!projectReportId) {
      return;
    }

    setLoading(true);
    setReport(null);
    setComments([]);
    replaceImageUrls({});

    try {
      const [detail, commentList] = await Promise.all([
        projectReportsApi.fetchProjectReport(projectReportId),
        projectReportsApi.fetchProjectReportComments(projectReportId),
      ]);
      setReport(detail);
      setComments(commentList);

      const urlEntries = await Promise.all(
        (detail.images ?? []).map(async (image) => {
          try {
            return [image.id, await uploadsApi.fetchImageContentUrl(image.id)] as const;
          } catch {
            return [image.id, ''] as const;
          }
        }),
      );
      replaceImageUrls(Object.fromEntries(urlEntries));
    } catch (error) {
      const text = error instanceof ApiError ? error.message : '加载项目日报详情失败';
      message.error(text);
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [message, projectReportId, replaceImageUrls]);

  useEffect(() => {
    if (!projectReportId) {
      setReport(null);
      setComments([]);
      replaceImageUrls({});
      setLoading(false);
      return;
    }
    void loadDetail();
  }, [loadDetail, projectReportId, replaceImageUrls]);

  useEffect(
    () => () => {
      Object.values(imageUrlsRef.current).forEach((url) => {
        if (url) URL.revokeObjectURL(url);
      });
    },
    [],
  );

  const createComment = async (content: string) => {
    if (!projectReportId) {
      return;
    }

    await projectReportsApi.createProjectReportComment(projectReportId, { content });
    message.success('点评已发布');
    await loadDetail();
  };

  return {
    loading,
    report,
    comments,
    imageUrls,
    canComment,
    reload: loadDetail,
    createComment,
  };
}
